/**
 * Página /admin/proveedores — registro GLOBAL cross-tenant de RUC→categoria_gasto.
 *
 * Para qué: un admin revisa las propuestas de IA y/o categoriza manualmente los
 * RUCs nuevos. La asignación se aplica a TODAS las invoices del histórico
 * (todos los tenants) con ese sender_ruc. Una sola revisión beneficia a toda
 * la plataforma.
 *
 * Filtros típicos para el workflow del admin:
 *  - status=sin_categoria + sort=most_seen → trabajar primero los más vistos.
 *  - source=ia → revisar las propuestas de la IA que están pendientes de OK.
 *  - search por nombre del proveedor para asignación puntual.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Building2, Search, Sparkles, UserCog, CheckCircle2, Hash, X, RefreshCw } from 'lucide-react';
import { SearchableCategorySelect } from '@/components/searchable-category-select';
import { toast } from 'sonner';
import { api, ApiCallError } from '@/lib/api';
import { CATEGORIA_GASTO } from '@shared/constants';
import { AdminShell } from '@/components/admin-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Categoria = (typeof CATEGORIA_GASTO)[number];

interface ProveedorRow {
  rucBase: string;
  razonSocial: string;
  categoria: Categoria | null;
  categoriaSource: 'ia' | 'admin' | null;
  categoriaAsignadaAt: string | null;
  asignadoPorEmail: string | null;
  totalInvoices: number;
  tenantsCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

interface Stats {
  total: number;
  sin_categoria: number;
  ia: number;
  admin: number;
}

const STATUS_OPTIONS = [
  { v: 'todos', l: 'Todos' },
  { v: 'sin_categoria', l: 'Sin categoría' },
  { v: 'categorizadas', l: 'Categorizadas' },
] as const;

const SOURCE_OPTIONS = [
  { v: 'todos', l: 'Cualquier origen' },
  { v: 'ia', l: 'Propuesta por IA' },
  { v: 'admin', l: 'Confirmada por admin' },
  { v: 'ninguna', l: 'Sin asignar' },
] as const;

const SORT_OPTIONS = [
  { v: 'most_seen', l: 'Más vistos primero' },
  { v: 'recent', l: 'Más recientes' },
  { v: 'alphabetic', l: 'Alfabético' },
] as const;

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(new Date(iso), 'dd MMM yyyy', { locale: es });
}

export function ProveedoresListPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<'todos' | 'sin_categoria' | 'categorizadas'>('todos');
  const [source, setSource] = useState<'todos' | 'ia' | 'admin' | 'ninguna'>('todos');
  const [sort, setSort] = useState<'most_seen' | 'recent' | 'alphabetic'>('most_seen');
  // Filtro por categoría específica (ej. 'otro' para revisar los que la IA puso
  // como Otros). '' = sin filtro. Nombre distinto a la mutation setCategoria.
  const [categoriaFilter, setCategoriaFilter] = useState<Categoria | ''>('');
  const [q, setQ] = useState('');

  const params = useMemo(() => {
    const p = new URLSearchParams({ status, source, sort, pageSize: '100' });
    if (categoriaFilter) p.set('categoria', categoriaFilter);
    if (q.trim()) p.set('q', q.trim());
    return p.toString();
  }, [status, source, sort, categoriaFilter, q]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-proveedores', params],
    queryFn: () =>
      api<{ items: ProveedorRow[]; total: number }>(`/api/admin/proveedores?${params}`),
  });

  const { data: stats } = useQuery({
    queryKey: ['admin-proveedores-stats'],
    queryFn: () => api<Stats>('/api/admin/proveedores/stats'),
  });

  const refreshCatalogo = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; proveedoresInsertados: number }>('/api/admin/proveedores/refresh-catalogo', {
        method: 'POST',
      }),
    onSuccess: (r) => {
      if (r.proveedoresInsertados === 0) {
        toast.info('Catálogo ya actualizado — no hay RUCs nuevos para agregar');
      } else {
        toast.success(
          `Catálogo actualizado — ${r.proveedoresInsertados} proveedor${r.proveedoresInsertados === 1 ? '' : 'es'} nuevo${r.proveedoresInsertados === 1 ? '' : 's'} agregado${r.proveedoresInsertados === 1 ? '' : 's'}`
        );
      }
      qc.invalidateQueries({ queryKey: ['admin-proveedores'] });
      qc.invalidateQueries({ queryKey: ['admin-proveedores-stats'] });
    },
    onError: () => toast.error('No se pudo actualizar el catálogo'),
  });

  const setCategoria = useMutation({
    mutationFn: ({ rucBase, categoria }: { rucBase: string; categoria: Categoria | null }) =>
      api<{ ok: boolean; invoicesActualizadas: number }>(`/api/admin/proveedores/${rucBase}`, {
        method: 'PATCH',
        body: JSON.stringify({ categoria }),
      }),
    onSuccess: (r) => {
      toast.success(
        `Categoría guardada — ${r.invoicesActualizadas} factura${r.invoicesActualizadas === 1 ? '' : 's'} actualizada${r.invoicesActualizadas === 1 ? '' : 's'}`
      );
      qc.invalidateQueries({ queryKey: ['admin-proveedores'] });
      qc.invalidateQueries({ queryKey: ['admin-proveedores-stats'] });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiCallError ? (e.body.error ?? 'Error') : 'No se pudo guardar la categoría'
      ),
  });

  const items = data?.items ?? [];

  return (
    <AdminShell>
      <div className="min-w-0 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Proveedores categorizados</h1>
            <p className="text-sm text-muted-foreground">
              Registro global de RUC → categoría. Lo que asignes acá se aplica a TODAS las facturas
              con ese proveedor (todos los tenants).
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshCatalogo.mutate()}
            disabled={refreshCatalogo.isPending}
            title="Re-procesa el historial de facturas y agrega al catálogo los RUCs que faltan"
          >
            <RefreshCw className={`size-4 mr-2 ${refreshCatalogo.isPending ? 'animate-spin' : ''}`} />
            Refresh catálogo
          </Button>
        </div>

        {/* KPIs */}
        {stats && (
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <Stat label="Total proveedores" value={stats.total} icon={<Building2 className="size-4 text-muted-foreground" />} />
            <Stat label="Sin categoría" value={stats.sin_categoria} highlight={stats.sin_categoria > 0} icon={<Hash className="size-4 text-warning" />} />
            <Stat label="Propuestos por IA" value={stats.ia} icon={<Sparkles className="size-4 text-primary" />} />
            <Stat label="Confirmados por admin" value={stats.admin} icon={<CheckCircle2 className="size-4 text-success" />} />
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o RUC…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-64 pl-8"
            />
            {q && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setQ('')}
              >
                <X className="size-4" />
              </button>
            )}
          </div>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={(v) => setSource(v as typeof source)}>
            <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SOURCE_OPTIONS.map((o) => (
                <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Filtro por categoría específica — usa el SearchableCategorySelect
              compartido (con buscador) porque son 38 opciones. */}
          <SearchableCategorySelect
            value={categoriaFilter || null}
            onChange={(c) => setCategoriaFilter(c ?? '')}
            placeholder="Cualquier categoría"
            nullLabel="— Cualquier categoría —"
            triggerClassName="w-52"
          />
          {categoriaFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCategoriaFilter('')}
              title="Limpiar filtro de categoría"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        {isLoading && <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>}
        {isError && <p className="text-sm text-destructive">No se pudo cargar el listado.</p>}

        {!isLoading && items.length === 0 && (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Sin proveedores con esos filtros.
          </p>
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proveedor</TableHead>
                  <TableHead className="text-right">Facturas</TableHead>
                  <TableHead className="text-right">Tenants</TableHead>
                  <TableHead>Última vez</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Origen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.rucBase}>
                    <TableCell className="max-w-[20rem]">
                      <div
                        className="truncate font-medium leading-tight"
                        title={p.razonSocial}
                      >
                        {p.razonSocial}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">{p.rucBase}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{p.totalInvoices}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.tenantsCount}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDate(p.lastSeenAt)}</TableCell>
                    <TableCell>
                      <SearchableCategorySelect
                        value={p.categoria}
                        disabled={setCategoria.isPending}
                        onChange={(categoria) => setCategoria.mutate({ rucBase: p.rucBase, categoria })}
                      />
                    </TableCell>
                    <TableCell>
                      {p.categoriaSource === 'admin' && (
                        <Badge variant="success" className="text-[10px]" title={p.asignadoPorEmail ?? ''}>
                          <UserCog className="mr-1 size-3" /> admin
                        </Badge>
                      )}
                      {p.categoriaSource === 'ia' && (
                        <Badge variant="secondary" className="text-[10px]">
                          <Sparkles className="mr-1 size-3" /> IA
                        </Badge>
                      )}
                      {!p.categoriaSource && (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {data && data.total > items.length && (
          <p className="text-xs text-muted-foreground">
            Mostrando {items.length} de {data.total}. Refiná los filtros para ver más.
          </p>
        )}
      </div>
    </AdminShell>
  );
}

// SearchableCategorySelect movido a components/searchable-category-select.tsx

function Stat({
  label, value, icon, highlight,
}: { label: string; value: number; icon?: React.ReactNode; highlight?: boolean }) {
  return (
    <div className={`rounded-md border border-border bg-background px-4 py-3 ${highlight ? 'border-warning/40' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        {icon}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
